/**
 * A guarded transition. `when` is the precondition (omit for unconditional);
 * `to` produces the next state. First matching transition wins.
 */
export interface Transition<State, Event> {
    readonly when?: (state: State, event: Event) => boolean;
    readonly to: (state: State, event: Event) => State;
}

/**
 * Build a `(state, event) => state` function from a table of guarded
 * transitions keyed by event type. Within each event's list, the first entry
 * whose `when` predicate matches (or is absent) fires; otherwise state is
 * unchanged.
 */
export function defineTransitions<State, Event extends { readonly type: string }>(table: {
    readonly [K in Event["type"]]: ReadonlyArray<Transition<State, Extract<Event, { readonly type: K }>>>;
}): (state: State, event: Event) => State {
    return (state, event) => {
        const transitions = table[event.type as Event["type"]] as ReadonlyArray<Transition<State, Event>> | undefined;
        if (!transitions) return state;
        for (const t of transitions) {
            if (!t.when || t.when(state, event)) return t.to(state, event);
        }
        return state;
    };
}
